import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {useUserSettingsDataContext} from "../../../../../../../providers/UserSettingsProvider";
import {TopViewWallAreaMeasurement} from "../../../models/editor";
import {TopViewWallAreaHelper} from "../../topViewWallArea/TopViewWallAreaHelper";
import {convertToMeters, convertToUnit} from "../../../../../../../utils/ConversionUtils";
import {maxFourDecimalsNumberRegexp} from "../../../../../../../utils/TextUtils";
import {UnitsType} from "../../../../../../../models/enums";
import {selectScale} from "../../scale/scaleSlice";
import EditValueModal from "../../../../../../../components/modal/EditValueModal";
import LengthInput from "../../../../../../../components/ui/LengthInput";

interface Props {
	isOpen: boolean;
	measurement: TopViewWallAreaMeasurement;
	onSave: (heights: Record<string, number | undefined>, defaultHeight: number | undefined) => void;
	onClose: () => void;
}

const SetWallHeightsModal: React.FC<Props> = ({isOpen, measurement, onSave, onClose}) => {
	const {t} = useTranslation();
	const {settings} = useUserSettingsDataContext();
	const scale = useSelector(selectScale);
	const [localHeights, setLocalHeights] = useState<Record<string, number | undefined>>({});
	const [defaultHeight, setDefaultHeight] = useState<number | undefined>(undefined);

	useEffect(() => {
		if (!isOpen) return;
		const initialDefault = measurement.defaultHeight !== undefined
			? (measurement.defaultHeight === 0 ? 0 : convertToUnit(measurement.defaultHeight, settings?.measurementSystem))
			: undefined;
		setDefaultHeight(initialDefault);

		const initialHeights: Record<string, number | undefined> = {};
		for (const [pointId, height] of Object.entries(measurement.heightMap)) {
			initialHeights[pointId] = height === 0 ? 0 : convertToUnit(height, settings?.measurementSystem);
		}
		setLocalHeights(initialHeights);
	}, [isOpen, measurement.heightMap, measurement.defaultHeight, settings?.measurementSystem]);

	const orderedPoints = TopViewWallAreaHelper.getOrderedUniquePoints(measurement);

	const isAllowedPrecision = (value: number) =>
		settings?.measurementSystem === UnitsType.IMPERIAL || maxFourDecimalsNumberRegexp.test(value.toString());

	const handleChange = (pointId: string, value: number | undefined) => {
		if (value === undefined) {
			setLocalHeights(prev => ({...prev, [pointId]: undefined}));
			return;
		}
		if (isAllowedPrecision(value)) {
			setLocalHeights(prev => ({...prev, [pointId]: value}));
		}
	};

	const handleDefaultHeightChange = (value: number | undefined) => {
		if (value === undefined || isAllowedPrecision(value)) {
			setDefaultHeight(value);
		}
	};

	const toMeters = (v: number) => v === 0 ? 0 : (convertToMeters(v, settings?.measurementSystem) ?? v);

	const handleSave = () => {
		const converted: Record<string, number | undefined> = {};
		for (const point of orderedPoints) {
			const displayValue = localHeights[point.id];
			if (displayValue !== undefined) {
				converted[point.id] = toMeters(displayValue);
			} else if (defaultHeight !== undefined) {
				converted[point.id] = toMeters(defaultHeight);
			}
		}
		const convertedDefault = defaultHeight !== undefined ? toMeters(defaultHeight) : undefined;
		onSave(converted, convertedDefault);
	};

	const toMetersForPreview = (v: number | undefined) => v !== undefined ? toMeters(v) : undefined;

	// Save is disabled only when some points have no individual height and no default to cover them
	const allCovered = orderedPoints.every(p => localHeights[p.id] !== undefined || defaultHeight !== undefined);
	const unitSuffix = settings?.measurementSystem === UnitsType.METRIC ? ` [${t("common.units.meterSymbol")}]` : "";

	const liveHeightMap: Record<string, number> = {};
	for (const point of orderedPoints) {
		const h = toMetersForPreview(localHeights[point.id]) ?? toMetersForPreview(defaultHeight);
		if (h !== undefined) {
			liveHeightMap[point.id] = h;
		}
	}
	const liveArea = TopViewWallAreaHelper.getFormattedValue(
		{...measurement, heightMap: liveHeightMap},
		scale,
		settings?.measurementSystem
	);

	return (
		<EditValueModal
			isOpen={isOpen}
			onClose={onClose}
			onSave={handleSave}
			title={t("editor.topViewWallArea.setHeightsModal.title")}
			disableSave={!allCovered}
		>
			<div style={{marginBottom: 16}}>
				<LengthInput
					label={`${t("editor.topViewWallArea.setHeightsModal.defaultHeight")}${unitSuffix}`}
					value={defaultHeight}
					unitsType={settings?.measurementSystem}
					onChange={handleDefaultHeightChange}
				/>
			</div>
			{orderedPoints.length > 0 && <hr style={{margin: "0 0 12px", border: "none", borderTop: "1px solid #e0e0e0"}}/>}
			{orderedPoints.map((point, index) => {
				const label = TopViewWallAreaHelper.getPointLabel(index);
				return (
					<div key={point.id} style={{margin: "0 auto 8px"}}>
						<LengthInput
							label={`${t("editor.topViewWallArea.heightPointItemPrefix")} ${label} ${t("editor.topViewWallArea.setHeightsModal.heightLabel")}${unitSuffix}`}
							value={localHeights[point.id]}
							unitsType={settings?.measurementSystem}
							onChange={(value) => handleChange(point.id, value)}
						/>
					</div>
				);
			})}
			{orderedPoints.length > 0 && (
				<div style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					background: "#f5f5f5",
					border: "1px solid #e0e0e0",
					borderRadius: 4,
					padding: "8px 12px",
					marginTop: 12,
					marginBottom: 16,
				}}>
					<span>{t("editor.topViewWallArea.setHeightsModal.totalArea")}</span>
					<span style={{fontWeight: 600}}>{liveArea}</span>
				</div>
			)}
		</EditValueModal>
	);
};

export default SetWallHeightsModal;
